import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSafeThemeClasses, useTheme, type ThemeVariant } from '@/shared/theme';
import { useUserState } from '@/features/user/operations/UserStateProvider';
import { useUpdateUser } from '@/features/user/operations/useUpdateUser';
import { dateUtils, logger } from '@/shared/utils';
import { useMediaQuery } from '@/shared/hooks';
import { ROUTES, ADMIN_ROUTES } from '@/shared/config';
import { PAGE_KEYS } from '@/shared/config/pageKeys';
import { Input } from '@/shared/components/ui';
import toast from 'react-hot-toast';

// Define the Board enum using string values for clarity
enum Board {
	PROFILE = 'PROFILE',
	SETTINGS = 'SETTINGS',
}

interface DashboardPageProps {
	section?: string;
}

interface ProfileProps {
	readonly name: string;
	readonly bio: string;
	readonly isAdmin: boolean;
	readonly email?: string;
	readonly joinDate?: string;
	readonly theme: ReturnType<typeof useSafeThemeClasses>;
}

interface SidePanelProps {
	setBoard: (board: Board) => void;
	currentBoard: Board;
	theme: ReturnType<typeof useSafeThemeClasses>;
	canSeeProfile: boolean;
	canSeeSettings: boolean;
	canSeeDrafts: boolean;
	canSeeAdmin: boolean;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ section }) => {
	const theme = useSafeThemeClasses();
	const navigate = useNavigate();

	// Get section from URL params if not provided as prop
	const params = useParams<{ section?: string }>();
	const actualSection = section || params?.section;

	// Determine initial board based on section prop or URL param
	const getInitialBoard = (): Board => {
		if (actualSection === 'settings') return Board.SETTINGS;
		if (actualSection === 'profile') return Board.PROFILE;
		if (actualSection === 'drafts') return Board.PROFILE; // Drafts shows in profile view for now
		return Board.PROFILE; // Default
	};

	// State to manage the current active board
	const [board, setBoard] = useState<Board>(getInitialBoard());

	// State for animations
	const [isVisible, setIsVisible] = useState<boolean>(false);

	// Get user data from user service
	const { user, refreshUser, hasEntitlement } = useUserState();
	const canSeeProfile = hasEntitlement(PAGE_KEYS.PROFILE);
	const canSeeSettings = hasEntitlement(PAGE_KEYS.SETTINGS);
	const canSeeDrafts = hasEntitlement(PAGE_KEYS.DRAFTS);

	// Compute profile data from user
	const profile = React.useMemo(() => {
		if (!user) {
			return {
				name: 'Guest User',
				email: '',
				bio: 'Please log in to view your profile.',
				joinDate: '',
				isAdmin: false,
			};
		}
		
		const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;
		const joinDate = user.created_at ? dateUtils.format(user.created_at, 'LONG') : 'Recently';
		
		return {
			name: fullName,
			email: user.email,
			bio: (user.data?.bio as string) || 'No bio available.',
			joinDate,
			isAdmin: user.is_admin === true || user.is_god === true,
		};
	}, [user]);

	useEffect(() => {
		// Animate content in after component mounts
		setIsVisible(true);
		logger.info('Dashboard accessed', 'DashboardPage', { section: section ?? 'default' });
		
		// Refresh user data on mount
		if (!user) {
			refreshUser();
		}
	}, [section, user, refreshUser]);

	// Effect to update board when section prop or URL param changes
	useEffect(() => {
		const currentSection = actualSection;
		if (currentSection) {
			// Old in-dashboard admin/analytics boards were consolidated into the
			// standalone /admin/* pages - redirect any stale links there.
			if (currentSection === 'admin' || currentSection === 'analytics') {
				navigate(ADMIN_ROUTES.DASHBOARD, { replace: true });
				return;
			}
			if (currentSection === 'settings') setBoard(Board.SETTINGS);
			else if (currentSection === 'profile') setBoard(Board.PROFILE);
			else if (currentSection === 'drafts') setBoard(Board.PROFILE); // Drafts shows in profile view for now

			logger.info('Dashboard section changed', 'DashboardPage', { section: currentSection });
		}
	}, [actualSection, navigate]);

	// Board switching here is local state, not a route change - it doesn't
	// go through ProtectedRoute's per-route requiredPageKey check the way
	// navigating to /profile or /settings directly would. If the active
	// board becomes denied (or was denied to start with), fall back to
	// whichever of the two is actually visible instead of silently
	// rendering a board the user shouldn't see.
	useEffect(() => {
		if (board === Board.PROFILE && !canSeeProfile && canSeeSettings) {
			setBoard(Board.SETTINGS);
		} else if (board === Board.SETTINGS && !canSeeSettings && canSeeProfile) {
			setBoard(Board.PROFILE);
		}
	}, [board, canSeeProfile, canSeeSettings]);

	return (
		<div className={`flex min-h-screen transition-opacity duration-500 ${theme.background} ${isVisible ? 'opacity-100' : 'opacity-0'} pb-20`}>
			{/* Background decoration */}
			<div className='fixed inset-0 overflow-hidden pointer-events-none'>
				<div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[0]} animate-pulse`}></div>
				<div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl ${theme.orbs[1]} animate-pulse`} style={{ animationDelay: '1s' }}></div>
			</div>

			{/* SidePanel is portaled to <body> - position:fixed breaks out of the
				viewport when nested inside a backdrop-blur ancestor (theme.background
				applies backdrop-blur, which establishes a new CSS containing block
				for fixed descendants per spec), which was pushing the sidebar fully
				off-screen and unreachable. It's desktop-only (lg:); on mobile,
				navigation happens via the horizontal tab row below instead of a
				second hamburger-triggered drawer, so there's only ever one menu
				control on screen (the global nav's) at once. */}
			{createPortal(
				<SidePanel
					setBoard={setBoard}
					currentBoard={board}
					theme={theme}
					canSeeProfile={canSeeProfile}
					canSeeSettings={canSeeSettings}
					canSeeDrafts={canSeeDrafts}
					canSeeAdmin={profile.isAdmin && hasEntitlement(PAGE_KEYS.ADMIN_DASHBOARD)}
				/>,
				document.body
			)}

			{/* Main Content Area - sidebar is permanently visible from lg: up */}
			<div className="relative flex-1 transition-all duration-300 ml-0 lg:ml-64">
				{/* Top Bar - Page Title (no toggle button: the global nav already owns
					the one hamburger on this page; mobile section switching happens via
					the tab row directly below) */}
				<div className={`sticky top-0 z-10 ${theme.header}`}>
					<div className="px-6 py-4">
						<h1 className={`text-2xl font-bold text-center ${theme.textHeading} tracking-tight`}>
							{board === Board.PROFILE && 'Profile'}
							{board === Board.SETTINGS && 'Settings'}
						</h1>
					</div>

					{/* Mobile section tabs - replaces the old off-canvas drawer on small
						screens so there's a single, always-visible way to switch sections
						instead of a redundant second hamburger. */}
					<nav className="lg:hidden flex gap-2 overflow-x-auto px-4 pb-3 -mt-1">
						{canSeeProfile && (
							<button
								onClick={() => setBoard(Board.PROFILE)}
								className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
									board === Board.PROFILE ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}` : `${theme.buttonGhost} ${theme.textSecondary}`
								}`}
							>
								Profile
							</button>
						)}
						{canSeeSettings && (
							<button
								onClick={() => setBoard(Board.SETTINGS)}
								className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
									board === Board.SETTINGS ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}` : `${theme.buttonGhost} ${theme.textSecondary}`
								}`}
							>
								Settings
							</button>
						)}
						{canSeeDrafts && (
							<Link
								to={ROUTES.DRAFTS}
								className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${theme.buttonGhost} ${theme.textSecondary}`}
							>
								Drafts
							</Link>
						)}
						{profile.isAdmin && hasEntitlement(PAGE_KEYS.ADMIN_DASHBOARD) && (
							<Link
								to={ADMIN_ROUTES.DASHBOARD}
								className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${theme.buttonGhost} ${theme.textSecondary}`}
							>
								Admin Panel
							</Link>
						)}
					</nav>
				</div>

				{/* Main Content with animation and glass morphism */}
				<div className="p-6 md:p-8 lg:p-10 animate-fadeIn min-h-[calc(100vh-120px)]">
					{/* Conditional Rendering Based on Current Board */}
					{board === Board.PROFILE && <Profile {...profile} theme={theme} />}
					{board === Board.SETTINGS && <Settings theme={theme} />}
				</div>
			</div>
		</div>
	);
};

// SidePanel Component - Modern Glass Morphism. Desktop-only (lg:) permanent
// rail; mobile navigation is handled by the tab row in the main content top
// bar instead, so there's no off-canvas drawer/second hamburger to maintain.
const SidePanel: React.FC<SidePanelProps> = ({
	setBoard,
	currentBoard,
	theme,
	canSeeProfile,
	canSeeSettings,
	canSeeDrafts,
	canSeeAdmin,
}) => {
	const selectBoard = (nextBoard: Board) => {
		setBoard(nextBoard);
	};

	// Desktop-only permanent rail - hidden entirely below lg: (mobile uses the
	// tab row instead), so this never needs to animate open/closed. Tailwind
	// v4 + DaisyUI's compiled CSS doesn't reliably let translate-x utility
	// classes win over each other for the standalone `translate` property
	// here, so the transform is still driven inline rather than via classes.
	const isDesktop = useMediaQuery('(min-width: 1024px)');

	return (
		<div
			className={`fixed top-[4.8rem] left-0 h-[calc(100%-4.8rem)] w-64 border-r z-20 transition-all duration-300 ease-in-out ${theme.sidebar}`}
			style={{ transform: isDesktop ? 'translateX(0)' : 'translateX(-100%)' }}
		>
			{/* Header with Logo/Brand - Glass effect */}
			<div className={`p-5 flex justify-between items-center border-b ${theme.separator}`}>
				<h2 className={`font-bold text-xl ${theme.textHeading} tracking-tight`}>Dashboard</h2>
			</div>

			{/* Navigation Menu - Modern spacing */}
			<nav className="p-4 space-y-1.5">
				{/* Profile Button - Modern with glass effect */}
				{canSeeProfile && (
				<button
					onClick={() => selectBoard(Board.PROFILE)}
					className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 group ${
						currentBoard === Board.PROFILE 
							? `${theme.navItemActive} shadow-lg scale-[1.02]` 
							: `${theme.navItem} ${theme.textPrimary} hover:bg-opacity-50 hover:scale-[1.01]`
					}`}
					aria-current={currentBoard === Board.PROFILE ? 'page' : undefined}
				>
					<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
						/>
					</svg>
					<span className={`ml-3 font-medium transition-opacity duration-300`}>Profile</span>
				</button>
				)}

				{/* Settings Button - Modern */}
				{canSeeSettings && (
				<button
					onClick={() => selectBoard(Board.SETTINGS)}
					className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 group ${
						currentBoard === Board.SETTINGS 
							? `${theme.navItemActive} shadow-lg scale-[1.02]` 
							: `${theme.navItem} ${theme.textPrimary} hover:bg-opacity-50 hover:scale-[1.01]`
					}`}
					aria-current={currentBoard === Board.SETTINGS ? 'page' : undefined}
				>
					<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
						/>
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
					</svg>
					<span className={`ml-3 font-medium transition-opacity duration-300`}>Settings</span>
				</button>
				)}

				{/* Drafts Button - Link to drafts page - Modern */}
				{canSeeDrafts && (
				<Link
					to={ROUTES.DRAFTS}
					className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 group ${theme.navItem} ${theme.textPrimary} hover:bg-opacity-50 hover:scale-[1.01]`}
				>
					<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
						/>
					</svg>
					<span className={`ml-3 font-medium transition-opacity duration-300`}>Drafts</span>
				</Link>
				)}

				{/* Conditionally Render Admin Button - Link to Admin Dashboard - Modern */}
				{canSeeAdmin && (
					<Link
						to={ADMIN_ROUTES.DASHBOARD}
						className={`w-full flex items-center p-3 rounded-xl transition-all duration-300 group ${theme.navItem} ${theme.textPrimary} hover:bg-opacity-50 hover:scale-[1.01]`}
					>
						<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
							/>
						</svg>
						<span className={`ml-3 font-medium transition-opacity duration-300`}>Admin Panel</span>
					</Link>
				)}
			</nav>
		</div>
	);
};

// Profile Component
const Profile: React.FC<ProfileProps> = ({ name, bio, isAdmin, email, joinDate, theme }) => {
	const [activities, setActivities] = useState<Array<{
		id: number | string;
		type?: string;
		activity_type?: string;
		description: string;
		created_at: string;
	}>>([]);
	const [loadingActivities, setLoadingActivities] = useState(true);
	const [isEditing, setIsEditing] = useState(false);
	const [editForm, setEditForm] = useState({
		first_name: name.split(' ')[0] || '',
		last_name: name.split(' ').slice(1).join(' ') || '',
		bio: bio || '',
		email: email || '',
		phone: '',
		timezone: 'UTC',
		language: 'en',
		profile_image_url: ''
	});
	const [saving, setSaving] = useState(false);
	const { user, refreshUser } = useUserState();
	const { updateUser, loading: updateLoading } = useUpdateUser();

	// Fetch user activities
	useEffect(() => {
		const loadActivities = async () => {
			try {
				const { userApiService } = await import('@/shared/services/userApiService');
				const response = await userApiService.getUserActivity({ limit: 5 });
				if (response.success && response.data?.activities) {
					// Map backend response (uses 'type') to frontend format
					const mappedActivities = response.data.activities.map((activity: { id: number | string; type?: string; activity_type?: string; description: string; created_at: string }) => ({
						id: activity.id,
						type: activity.type || activity.activity_type,
						description: activity.description,
						created_at: activity.created_at
					}));
					setActivities(mappedActivities);
				}
			} catch (error) {
				logger.error('Failed to load activities', 'DashboardPage', { error });
			} finally {
				setLoadingActivities(false);
			}
		};

		loadActivities();
	}, []);

	// Initialize edit form when user data changes
	useEffect(() => {
		if (user && !isEditing) {
			const userData = user.data as Record<string, unknown> | undefined;
			const userMetadata = userData?.user_metadata as Record<string, unknown> | undefined;
			// Phone can be in user.data.phone or user.data.user_metadata?.phone
			const phone = (userData?.phone as string) || 
				(userMetadata?.phone as string) || '';
			// Timezone and language can be in user.data or user.data.user_metadata
			const timezone = (userData?.timezone as string) || 
				(userMetadata?.timezone as string) || 'UTC';
			const language = (userData?.language as string) || 
				(userMetadata?.language as string) || 'en';
			
			setEditForm({
				first_name: user.first_name || '',
				last_name: user.last_name || '',
				bio: (user.data?.bio as string) || '',
				email: user.email || '',
				phone: phone,
				timezone: timezone,
				language: language,
				profile_image_url: (user.data?.profile_image_url as string) || ''
			});
		}
	}, [user, isEditing]);

	// Handle profile update
	const handleSaveProfile = async () => {
		setSaving(true);
		try {
			const result = await updateUser({
				email: editForm.email,
				first_name: editForm.first_name,
				last_name: editForm.last_name,
				bio: editForm.bio,
				phone: editForm.phone,
				timezone: editForm.timezone,
				language: editForm.language,
				profile_image_url: editForm.profile_image_url
			});
			
			if (result) {
				toast.success('Profile updated successfully');
				await refreshUser();
				setIsEditing(false);
			} else {
				toast.error('Failed to update profile');
			}
		} catch (error) {
			toast.error('Failed to update profile');
			logger.error('Profile update error', 'DashboardPage', { error });
		} finally {
			setSaving(false);
		}
	};

	// Get avatar initials - properly handle name
	const getInitials = (fullName: string): string => {
		const parts = fullName.trim().split(' ').filter(Boolean);
		if (parts.length === 0) return 'U';
		if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	};

	return (
		<div className="max-w-3xl mx-auto space-y-6">
			{/* Profile Header - Glass Morphism */}
			<div className={`rounded-2xl p-8 mb-6 transition-all duration-300 hover:shadow-2xl ${theme.card}`}>
				<div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
					{/* Profile Avatar */}
					<div className={`w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold ${theme.avatar}`}>
						{getInitials(name)}
					</div>
					
					{/* Basic Info */}
					<div className="flex-grow text-center sm:text-left">
						<div className="flex flex-col items-center sm:flex-row sm:items-center justify-between gap-2 mb-2">
							{!isEditing ? (
								<>
									<h1 className={`text-2xl font-bold mb-1 sm:mb-0 ${theme.textHeading}`}>{name}</h1>
									<button
										onClick={() => setIsEditing(true)}
										className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${theme.buttonSecondary} hover:scale-105 hover:shadow-lg active:scale-95`}
									>
										Edit Profile
									</button>
								</>
							) : (
								<div className="w-full space-y-4">
									{/* Basic Information Section */}
									<div>
										<h3 className={`${theme.textLabel}`}>Basic Information</h3>
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
											<Input
												type="text"
												placeholder="First Name"
												value={editForm.first_name}
												onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
												className={theme.input}
											/>
											<Input
												type="text"
												placeholder="Last Name"
												value={editForm.last_name}
												onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
												className={theme.input}
											/>
										</div>
									</div>

									{/* Contact Information Section */}
									<div>
										<h3 className={`${theme.textLabel}`}>Contact Information</h3>
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
											<Input
												type="email"
												placeholder="Email"
												value={editForm.email}
												onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
												className={theme.input}
											/>
											<Input
												type="tel"
												placeholder="Phone"
												value={editForm.phone}
												onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
												className={theme.input}
											/>
										</div>
									</div>

									{/* Profile Details Section */}
									<div>
										<h3 className={`${theme.textLabel}`}>Profile Details</h3>
										<Input
											type="text"
											placeholder="Bio"
											value={editForm.bio}
											onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
											className={theme.input}
										/>
										<Input
											type="url"
											placeholder="Profile Image URL"
											value={editForm.profile_image_url}
											onChange={(e) => setEditForm({ ...editForm, profile_image_url: e.target.value })}
											className={theme.input}
										/>
									</div>

									{/* Preferences Section */}
									<div>
										<h3 className={`${theme.textLabel}`}>Preferences</h3>
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
											<div>
												<label className={theme.textLabel}>Timezone</label>
												<select
													value={editForm.timezone}
													onChange={(e) => setEditForm({ ...editForm, timezone: e.target.value })}
													className={theme.select}
												>
													<option value="UTC">UTC</option>
													<option value="America/New_York">Eastern Time (ET)</option>
													<option value="America/Chicago">Central Time (CT)</option>
													<option value="America/Denver">Mountain Time (MT)</option>
													<option value="America/Los_Angeles">Pacific Time (PT)</option>
													<option value="Europe/London">London (GMT)</option>
													<option value="Europe/Paris">Paris (CET)</option>
													<option value="Asia/Tokyo">Tokyo (JST)</option>
													<option value="Asia/Shanghai">Shanghai (CST)</option>
													<option value="Asia/Kolkata">Mumbai (IST)</option>
												</select>
											</div>
											<div>
												<label className={theme.textLabel}>Language</label>
												<select
													value={editForm.language}
													onChange={(e) => setEditForm({ ...editForm, language: e.target.value })}
													className={theme.select}
												>
													<option value="en">English</option>
													<option value="es">Spanish</option>
													<option value="fr">French</option>
													<option value="de">German</option>
													<option value="zh">Chinese</option>
													<option value="ja">Japanese</option>
													<option value="hi">Hindi</option>
												</select>
											</div>
										</div>
									</div>

									{/* Action Buttons - Modern */}
									<div className="flex gap-3 pt-6 border-t border-opacity-20">
										<button
											onClick={handleSaveProfile}
											disabled={saving || updateLoading}
											className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${theme.buttonPrimary} ${theme.buttonTextPrimary} disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 hover:shadow-lg active:scale-95`}
										>
											{saving || updateLoading ? 'Saving...' : 'Save Changes'}
										</button>
										<button
											onClick={() => {
												setIsEditing(false);
												// Reset form
												if (user) {
													setEditForm({
														first_name: user.first_name || '',
														last_name: user.last_name || '',
														bio: (user.data?.bio as string) || '',
														email: user.email || '',
														phone: (user.data?.phone as string) || '',
														timezone: (user.data?.timezone as string) || 'UTC',
														language: (user.data?.language as string) || 'en',
														profile_image_url: (user.data?.profile_image_url as string) || ''
													});
												}
											}}
											className={`px-6 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${theme.buttonGhost} hover:scale-105 active:scale-95`}
										>
											Cancel
										</button>
									</div>
								</div>
							)}
							{!isEditing && isAdmin && (
								<span className={`px-2 py-1 text-xs rounded-full font-medium ${theme.badge}`}>Administrator</span>
							)}
						</div>
						{!isEditing && (
							<>
								<p className={`text-sm mb-2 ${theme.textSecondary}`}>{email}</p>
								{user?.data?.phone && (
									<p className={`text-sm mb-2 ${theme.textSecondary}`}>Phone: {user.data.phone as string}</p>
								)}
								<p className={`mb-2 ${theme.textPrimary}`}>{bio}</p>
								<div className="flex flex-wrap gap-4 text-sm mt-4">
									{user?.data?.timezone && typeof user.data.timezone === 'string' ? (
										<span className={theme.textSecondary}>Timezone: {user.data.timezone}</span>
									) : null}
									{user?.data?.language && typeof user.data.language === 'string' ? (
										<span className={theme.textSecondary}>Language: {user.data.language}</span>
									) : null}
								</div>
								<p className={`text-sm mt-4 ${theme.textSecondary}`}>Member since {joinDate}</p>
							</>
						)}
					</div>
				</div>
			</div>
			
			{/* Recent Activity Section - Glass Morphism */}
			<div className={`rounded-2xl p-8 mb-6 transition-all duration-300 hover:shadow-2xl ${theme.card}`}>
				<h2 className={`text-2xl font-bold mb-6 ${theme.textHeading} tracking-tight`}>Recent Activity</h2>
				{loadingActivities ? (
					<div className="flex items-center justify-center py-8">
						<div className={`animate-spin rounded-full h-6 w-6 border-b-2 ${theme.textAccent}`}></div>
					</div>
				) : activities.length === 0 ? (
					<p className={`text-sm ${theme.textMuted} py-4`}>No recent activity</p>
				) : (
					<ul className="space-y-3">
						{activities.map((activity) => {
							const timeAgo = activity.created_at 
								? dateUtils.timeAgo(new Date(activity.created_at))
								: 'Recently';
							const activityType = activity.type || activity.activity_type || 'activity';
							return (
								<li key={activity.id} className="flex items-start gap-3">
									<div className={`w-2 h-2 rounded-full mt-2 ${theme.textAccent}`}></div>
									<div>
										<p className={`font-medium ${theme.textPrimary}`}>{activity.description || activityType}</p>
										<p className={`text-sm ${theme.textSecondary}`}>{timeAgo}</p>
									</div>
								</li>
							);
						})}
					</ul>
				)}
			</div>
		
		</div>
	);
};

// Modern Toggle Switch Component
interface ToggleSwitchProps {
	id: string;
	label: string;
	description?: string;
	checked: boolean;
	onChange: (checked: boolean) => void;
	theme: ReturnType<typeof useSafeThemeClasses>;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ id, label, description, checked, onChange, theme }) => {
	return (
		<div className="flex items-center justify-between py-4 border-b border-opacity-10 last:border-0 group">
			<div className="flex-1">
				<label htmlFor={id} className={`block font-medium cursor-pointer ${theme.textPrimary} mb-1 group-hover:${theme.textAccent} transition-colors duration-200`}>
					{label}
				</label>
				{description && (
					<p className={`text-sm ${theme.textSecondary}`}>{description}</p>
				)}
			</div>
			<button
				type="button"
				role="switch"
				aria-checked={checked}
				onClick={() => onChange(!checked)}
				className={`relative inline-flex h-7 w-14 items-center rounded-full transition-all duration-300 shadow-md hover:shadow-lg ${theme.settingsToggleFocusRing} ${
					checked ? theme.settingsToggleTrackOn : theme.settingsToggleTrackOff
				}`}
			>
				<span
					className={`inline-block h-5 w-5 transform rounded-full transition-all duration-300 ${theme.settingsToggleKnob} ${
						checked ? 'translate-x-8 scale-100' : 'translate-x-1 scale-100'
					}`}
				/>
			</button>
		</div>
	);
};

// Modern Radio Option Component
interface RadioOptionProps {
	id: string;
	value: string;
	label: string;
	description?: string;
	checked: boolean;
	onChange: (value: string) => void;
	theme: ReturnType<typeof useSafeThemeClasses>;
}

const RadioOption: React.FC<RadioOptionProps> = ({ id, value, label, description, checked, onChange, theme }) => {
	return (
		<div 
			onClick={() => onChange(value)}
			className={`flex items-start p-5 rounded-xl cursor-pointer transition-all duration-300 group ${
				checked
					? theme.settingsRadioCardSelected
					: `${theme.card} ${theme.settingsRadioCardUnselected}`
			}`}
		>
			<div className={`flex-shrink-0 mt-0.5 mr-4`}>
				<div
					className={`relative w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center ${
						checked ? theme.settingsRadioOuterChecked : theme.settingsRadioOuterUnchecked
					}`}
				>
					{checked && <div className={`w-3 h-3 rounded-full ${theme.settingsRadioInnerDot}`} />}
				</div>
			</div>
			<div className="flex-1">
				<label htmlFor={id} className={`block font-semibold cursor-pointer ${checked ? theme.textHeading : theme.textPrimary} mb-1 transition-colors duration-200`}>
					{label}
				</label>
				{description && (
					<p className={`text-sm ${theme.textSecondary}`}>{description}</p>
				)}
			</div>
		</div>
	);
};

// Theme Style Dropdown - a themed dropdown button + menu (same pattern as
// Navigation's "Fun Tools" dropdown) for picking the Glass/Fantasy visual
// variant, replacing the old pair of radio cards.
const VARIANT_OPTIONS: { value: ThemeVariant; label: string; description: string; icon: string }[] = [
	{
		value: 'glass',
		label: 'Glass',
		description: 'Clean, modern frosted-glass interface',
		icon: 'M9 3v18M3 9h18M4 4h16v16H4z',
	},
	{
		value: 'fantasy',
		label: 'Fantasy',
		description: 'Ornate storybook interface with parchment and jewel tones',
		icon: 'M12 2l2.5 6.5L21 11l-6.5 2.5L12 20l-2.5-6.5L3 11l6.5-2.5z',
	},
];

interface ThemeStyleDropdownProps {
	value: ThemeVariant;
	onChange: (value: ThemeVariant) => void;
	theme: ReturnType<typeof useSafeThemeClasses>;
	menuRef: React.RefObject<HTMLDivElement | null>;
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
}

const ThemeStyleDropdown: React.FC<ThemeStyleDropdownProps> = ({ value, onChange, theme, menuRef, isOpen, setIsOpen }) => {
	const current = VARIANT_OPTIONS.find((o) => o.value === value) ?? VARIANT_OPTIONS[0];

	return (
		<div className="relative" ref={menuRef}>
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				className={`w-full flex items-center justify-between gap-3 p-4 rounded-xl transition-all duration-300 ${theme.input} ${theme.textPrimary}`}
			>
				<span className="flex items-center gap-3">
					<svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={current.icon} />
					</svg>
					<span className="text-left">
						<span className={`block font-semibold ${theme.textHeading}`}>{current.label}</span>
						<span className={`block text-sm ${theme.textSecondary}`}>{current.description}</span>
					</span>
				</span>
				<svg
					className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
				</svg>
			</button>

			{isOpen && (
				<ul
					role="listbox"
					className={`absolute left-0 right-0 top-full z-20 mt-2 py-2 rounded-xl overflow-hidden ${theme.navDropdown}`}
				>
					{VARIANT_OPTIONS.map((option) => (
						<li key={option.value} role="option" aria-selected={option.value === value}>
							<button
								type="button"
								onClick={() => {
									onChange(option.value);
									setIsOpen(false);
								}}
								className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200 ${theme.navItem} ${
									option.value === value ? theme.textHeading : theme.textPrimary
								}`}
							>
								<svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={option.icon} />
								</svg>
								<span>
									<span className="block font-semibold">{option.label}</span>
									<span className={`block text-sm ${theme.textSecondary}`}>{option.description}</span>
								</span>
								{option.value === value && (
									<svg className={`w-4 h-4 shrink-0 ml-auto ${theme.textAccent}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
									</svg>
								)}
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

// Password Input with Visibility Toggle
interface PasswordInputProps {
	id: string;
	label: string;
	placeholder: string;
	value: string;
	onChange: (value: string) => void;
	theme: ReturnType<typeof useSafeThemeClasses>;
}

const PasswordInput: React.FC<PasswordInputProps> = ({ id, label, placeholder, value, onChange, theme }) => {
	const [showPassword, setShowPassword] = useState(false);
	
	return (
		<div className="form-control">
			<label className="label" htmlFor={id}>
				<span className={`label-text font-medium ${theme.textLabel}`}>{label}</span>
			</label>
			<div className="relative">
				<input
					type={showPassword ? "text" : "password"}
					id={id}
					placeholder={placeholder}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					className={`w-full px-4 py-3 pr-12 rounded-xl ${theme.input} ${theme.textPrimary} transition-all duration-300 ${theme.settingsPasswordInputFocus}`}
				/>
				<button
					type="button"
					onClick={() => setShowPassword(!showPassword)}
					className={`absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all duration-300 ${theme.buttonGhost} hover:scale-110 active:scale-95`}
					aria-label={showPassword ? "Hide password" : "Show password"}
				>
					{showPassword ? (
						<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m0 0A9.97 9.97 0 015.12 5.12m3.29 3.29L12 12m-3.59-3.59L12 12m0 0l3.59 3.59M12 12l3.59-3.59m0 0a9.953 9.953 0 012.828-2.828M15.59 8.41l3.29-3.29" />
						</svg>
					) : (
						<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
						</svg>
					)}
				</button>
			</div>
		</div>
	);
};

// Settings Component
interface SettingsProps {
	readonly theme: ReturnType<typeof useSafeThemeClasses>;
}

const Settings: React.FC<SettingsProps> = ({ theme }) => {
	// mode/variant come straight from the live theme context - it's already
	// the real, cookie-persisted source of truth for what's actually
	// rendering on screen. This form used to keep its own local copies
	// (appearance/themeVariant), seeded only from a separately-saved
	// backend settings record - so changing the theme anywhere else (the
	// navbar toggle, for instance) updated the live theme immediately but
	// left this form showing whatever was last explicitly saved *here*,
	// stale and out of sync until the next save. Reading mode/variant
	// directly means this form can never disagree with what's on screen.
	const { mode, variant, setTheme: setThemeMode, setVariant } = useTheme();
	const [isVariantMenuOpen, setIsVariantMenuOpen] = useState(false);
	const variantMenuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isVariantMenuOpen) return;
		const handleClickOutside = (event: MouseEvent) => {
			if (variantMenuRef.current && !variantMenuRef.current.contains(event.target as Node)) {
				setIsVariantMenuOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isVariantMenuOpen]);

	const [emailPrefs, setEmailPrefs] = useState({
		marketing: true,
		productUpdates: true,
		securityAlerts: true,
	});


	const [privacy, setPrivacy] = useState({
		publicProfile: true,
		activityTracking: true,
	});
	
	const [passwords, setPasswords] = useState({
		current: '',
		new: '',
		confirm: '',
	});

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [changingPassword, setChangingPassword] = useState(false);

	// Load settings from backend on mount
	useEffect(() => {
		const loadSettings = async () => {
			try {
				setLoading(true);
				const { userApiService } = await import('@/shared/services/userApiService');
				const response = await userApiService.getUserSettings();
				
				if (response.success && response.data?.settings) {
					const settings = response.data.settings;
					
					// Map backend settings to frontend state
					if (settings.notifications) {
						setEmailPrefs({
							marketing: settings.notifications.email ?? true,
							productUpdates: settings.notifications.email ?? true,
							securityAlerts: settings.notifications.email ?? true,
						});
					}
					
					// Appearance itself is NOT read from here - mode/variant from the
					// live theme context are always current and already what's on
					// screen, so there's nothing to hydrate for them.
					if (settings.privacy) {
						setPrivacy({
							publicProfile: settings.privacy.profile_public ?? true,
							activityTracking: settings.privacy.show_activity ?? true,
						});
					}
				}
			} catch (error) {
				logger.error('Failed to load settings', 'DashboardPage', { error });
			} finally {
				setLoading(false);
			}
		};

		loadSettings();
	}, []);

	// Save settings to backend when they change
	const saveSettings = async (partialSettings: {
		email_preferences?: Record<string, unknown>;
		appearance_settings?: Record<string, unknown>;
		privacy_settings?: Record<string, unknown>;
	}) => {
		try {
			setSaving(true);
			const { userApiService } = await import('@/shared/services/userApiService');
			
			// Build complete settings object with current state + partial updates
			const backendSettings: Record<string, unknown> = {};
			
			if (partialSettings.email_preferences) {
				backendSettings.email_preferences = {
					marketing_emails: emailPrefs.marketing,
					product_updates: emailPrefs.productUpdates,
					security_alerts: emailPrefs.securityAlerts,
					...partialSettings.email_preferences,
				};
			}
			
			if (partialSettings.appearance_settings) {
				backendSettings.appearance_settings = {
					theme: mode,
					variant,
					...partialSettings.appearance_settings,
				};
			}
			
			if (partialSettings.privacy_settings) {
				backendSettings.privacy_settings = {
					public_profile: privacy.publicProfile,
					activity_tracking: privacy.activityTracking,
					...partialSettings.privacy_settings,
				};
			}

			// The backend expects the settings directly, not nested
			const response = await userApiService.updateUserSettings(backendSettings);
			
			if (response.success) {
				toast.success('Settings saved successfully');
			} else {
				toast.error(response.error?.message || 'Failed to save settings');
			}
		} catch (error) {
			logger.error('Failed to save settings', 'DashboardPage', { error });
			toast.error('Failed to save settings');
		} finally {
			setSaving(false);
		}
	};

	const handlePasswordChange = async (e: React.FormEvent) => {
		e.preventDefault();

		if (passwords.new !== passwords.confirm) {
			toast.error('New password and confirmation do not match');
			return;
		}
		if (passwords.new.length < 6) {
			toast.error('New password must be at least 6 characters');
			return;
		}

		try {
			setChangingPassword(true);
			const { userApiService } = await import('@/shared/services/userApiService');
			const response = await userApiService.changePassword({
				current_password: passwords.current,
				new_password: passwords.new,
			});

			if (response.success) {
				toast.success('Password updated successfully');
				setPasswords({ current: '', new: '', confirm: '' });
			} else {
				toast.error(response.error?.message || 'Failed to update password');
				logger.error('Failed to update password', 'DashboardPage', { error: response.error });
			}
		} catch (error) {
			toast.error('Network error. Please try again.');
			logger.error('Network error changing password', 'DashboardPage', { error });
		} finally {
			setChangingPassword(false);
		}
	};

	return (
		<div className="max-w-3xl mx-auto space-y-6">
			{/* Account Settings - Glass Morphism */}
			<div className={`rounded-2xl p-8 mb-6 transition-all duration-300 hover:shadow-2xl ${theme.card}`}>
				<h2 className={`text-2xl font-bold mb-8 ${theme.textHeading} tracking-tight`}>Account Settings</h2>
				
				{/* Email Preference Form */}
				<div className="mb-8">
					<h3 className={`text-lg font-semibold mb-6 ${theme.textHeading} tracking-tight`}>Email Preferences</h3>
					<div className="space-y-1">
						<ToggleSwitch
							id="marketing-emails"
							label="Marketing emails"
							description="Receive updates about new features and promotions"
							checked={emailPrefs.marketing}
							onChange={(checked) => {
								setEmailPrefs({ ...emailPrefs, marketing: checked });
								saveSettings({ email_preferences: { marketing_emails: checked } });
							}}
							theme={theme}
						/>
						<ToggleSwitch
							id="product-updates"
							label="Product updates"
							description="Get notified about new features and improvements"
							checked={emailPrefs.productUpdates}
							onChange={(checked) => {
								setEmailPrefs({ ...emailPrefs, productUpdates: checked });
								saveSettings({ email_preferences: { product_updates: checked } });
							}}
							theme={theme}
						/>
						<ToggleSwitch
							id="security-alerts"
							label="Security alerts"
							description="Important security notifications and login alerts"
							checked={emailPrefs.securityAlerts}
							onChange={(checked) => {
								setEmailPrefs({ ...emailPrefs, securityAlerts: checked });
								saveSettings({ email_preferences: { security_alerts: checked } });
							}}
							theme={theme}
						/>
					</div>
				</div>
				
				{/* Appearance Settings */}
				<div className="mb-8">
					<h3 className={`text-lg font-semibold mb-6 ${theme.textHeading} tracking-tight`}>Appearance</h3>
					<div className="space-y-3">
						<RadioOption
							id="theme-light"
							value="light"
							label="Light theme"
							description="Clean and bright interface"
							checked={mode === 'light'}
							onChange={(value) => {
								setThemeMode(value as 'light' | 'dark' | 'system'); // Apply theme site-wide immediately
								saveSettings({ appearance_settings: { theme: value } });
							}}
							theme={theme}
						/>
						<RadioOption
							id="theme-dark"
							value="dark"
							label="Dark theme"
							description="Easy on the eyes in low light"
							checked={mode === 'dark'}
							onChange={(value) => {
								setThemeMode(value as 'light' | 'dark' | 'system'); // Apply theme site-wide immediately
								saveSettings({ appearance_settings: { theme: value } });
							}}
							theme={theme}
						/>
						<RadioOption
							id="theme-system"
							value="system"
							label="System preference"
							description="Match your device settings"
							checked={mode === 'system'}
							onChange={(value) => {
								setThemeMode(value as 'light' | 'dark' | 'system'); // Apply theme site-wide immediately
								saveSettings({ appearance_settings: { theme: value } });
							}}
							theme={theme}
						/>
					</div>
				</div>

				{/* Theme Style Settings */}
				<div className="mb-8">
					<h3 className={`text-lg font-semibold mb-6 ${theme.textHeading} tracking-tight`}>Theme Style</h3>
					<ThemeStyleDropdown
						value={variant}
						onChange={(value) => {
							setVariant(value);
							saveSettings({ appearance_settings: { variant: value } });
						}}
						theme={theme}
						menuRef={variantMenuRef}
						isOpen={isVariantMenuOpen}
						setIsOpen={setIsVariantMenuOpen}
					/>
				</div>

				{/* Account Privacy */}
				<div className="mb-6">
					<h3 className={`text-lg font-semibold mb-6 ${theme.textHeading} tracking-tight`}>Privacy</h3>
					<div className="space-y-1">
						<ToggleSwitch
							id="profile-visibility"
							label="Public profile"
							description="Allow others to view your profile information"
							checked={privacy.publicProfile}
							onChange={(checked) => {
								setPrivacy({ ...privacy, publicProfile: checked });
								saveSettings({ privacy_settings: { public_profile: checked } });
							}}
							theme={theme}
						/>
						<ToggleSwitch
							id="activity-tracking"
							label="Allow activity tracking"
							description="Help us improve by sharing anonymous usage data"
							checked={privacy.activityTracking}
							onChange={(checked) => {
								setPrivacy({ ...privacy, activityTracking: checked });
								saveSettings({ privacy_settings: { activity_tracking: checked } });
							}}
							theme={theme}
						/>
					</div>
				</div>

				{/* Save All Settings Button */}
				<div className="pt-6 border-t border-opacity-10">
					<button
						onClick={() => saveSettings({
							email_preferences: {},
							appearance_settings: {},
							privacy_settings: {}
						})}
						disabled={saving || loading}
						className={`w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:scale-105 hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2`}
					>
						{saving ? (
							<>
								<div className={`animate-spin rounded-full h-5 w-5 border-b-2 ${theme.textPrimary}`}></div>
								<span>Saving...</span>
							</>
						) : (
							<>
								<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
								</svg>
								<span>Save All Settings</span>
							</>
						)}
					</button>
				</div>
			</div>
			
			{/* Password Section - Modern Glass Morphism */}
			<div className={`rounded-2xl p-8 transition-all duration-300 hover:shadow-2xl ${theme.card}`}>
				<h2 className={`text-2xl font-bold mb-8 ${theme.textHeading} tracking-tight`}>Password Settings</h2>
				<form className="space-y-6" onSubmit={handlePasswordChange}>
					<PasswordInput
						id="current-password"
						label="Current Password"
						placeholder="Enter your current password"
						value={passwords.current}
						onChange={(value) => setPasswords({ ...passwords, current: value })}
						theme={theme}
					/>
					<PasswordInput
						id="new-password"
						label="New Password"
						placeholder="Enter your new password"
						value={passwords.new}
						onChange={(value) => setPasswords({ ...passwords, new: value })}
						theme={theme}
					/>
					<PasswordInput
						id="confirm-password"
						label="Confirm New Password"
						placeholder="Confirm your new password"
						value={passwords.confirm}
						onChange={(value) => setPasswords({ ...passwords, confirm: value })}
						theme={theme}
					/>
					
					{/* Password Strength Indicator */}
					{passwords.new && (
						<div className="space-y-2">
							<div className="flex items-center justify-between text-xs">
								<span className={theme.textSecondary}>Password strength</span>
								<span className={theme.textSecondary}>
									{passwords.new.length < 8 ? 'Weak' : passwords.new.length < 12 ? 'Medium' : 'Strong'}
								</span>
							</div>
							<div className={`h-2 rounded-full overflow-hidden ${theme.settingsPasswordMeterTrack}`}>
								<div
									className={`h-full transition-all duration-300 rounded-full ${
										passwords.new.length < 8
											? `${theme.settingsPasswordMeterWeak} w-1/3`
											: passwords.new.length < 12
												? `${theme.settingsPasswordMeterMedium} w-2/3`
												: `${theme.settingsPasswordMeterStrong} w-full`
									}`}
								/>
							</div>
						</div>
					)}
					
					<div className="form-control mt-8">
						<button
							type="submit"
							disabled={changingPassword || !passwords.current || !passwords.new || passwords.new !== passwords.confirm}
							className={`w-full py-3.5 px-6 text-center rounded-xl font-medium transition-all duration-300 ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:scale-105 hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100`}
						>
							{changingPassword ? 'Updating...' : 'Update Password'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default DashboardPage;
